import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { campaignBudgetApiErrorMessage } from '@/api/campaignBudgetUpdate';
import {
  buildCampaignSettingsPatch,
  isEditableCampaignState,
  saveCampaignSettings,
  type CampaignSettingsDraft,
} from '@/api/campaignSettingsUpdate';
import type { SpBiddingStrategy } from '@/types/spCampaignCreate';
import { campaignAdProductLabel, type SpCampaignNormalized } from '@/types/spCampaigns';
import { validateCampaignBudget } from '@/utils/campaignCreate';
import {
  campaignBiddingStrategyCopy,
  campaignBiddingStrategyFromRaw,
  spCampaignTargetingTypeLabel,
} from '@/utils/spAdGroupCreate';

const BIDDING_STRATEGY_OPTIONS: SpBiddingStrategy[] = ['LEGACY_FOR_SALES', 'AUTO_FOR_SALES', 'MANUAL'];

type EditableField = 'name' | 'state' | 'biddingStrategy' | 'budget';

type SettingsRow = { label: string; value: string; field?: EditableField };

type CampaignSettingsSectionProps = {
  profileId: string;
  campaign: SpCampaignNormalized;
  /** Refresh the campaign after settings are saved. */
  onUpdated?: () => void;
};

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function campaignSettingsRows(campaign: SpCampaignNormalized): SettingsRow[] {
  const raw = campaign.raw;
  const budgetObj =
    raw.budget && typeof raw.budget === 'object'
      ? (raw.budget as Record<string, unknown>)
      : null;

  return [
    { label: 'Campaign ID', value: campaign.campaignId || displayValue(raw.campaignId) },
    { label: 'Name', value: campaign.name || displayValue(raw.name), field: 'name' },
    { label: 'Ad product', value: campaignAdProductLabel(campaign.adProduct) },
    { label: 'State', value: campaign.state || displayValue(raw.state), field: 'state' },
    { label: 'Targeting type', value: spCampaignTargetingTypeLabel(campaign) },
    ...(campaign.adProduct === 'SP'
      ? [
          {
            label: 'Bidding strategy',
            value: campaignBiddingStrategyCopy(campaignBiddingStrategyFromRaw(raw)).label,
            field: 'biddingStrategy' as const,
          },
        ]
      : []),
    {
      label: 'Daily budget',
      value:
        budgetObj?.budget != null
          ? displayValue(budgetObj.budget)
          : `${campaign.budget} (${campaign.budgetType})`,
      field: 'budget',
    },
    {
      label: 'Budget type',
      value: displayValue(budgetObj?.budgetType ?? campaign.budgetType),
    },
    { label: 'Start date', value: campaign.startDate || displayValue(raw.startDate) },
    { label: 'End date', value: campaign.endDate || displayValue(raw.endDate) },
    {
      label: 'Marketplace budget allocation',
      value: displayValue(raw.marketplaceBudgetAllocation),
    },
    { label: 'Off-Amazon settings', value: displayValue(raw.offAmazonSettings) },
    { label: 'Tags', value: displayValue(raw.tags) },
  ];
}

function draftFromCampaign(campaign: SpCampaignNormalized): CampaignSettingsDraft {
  return {
    name: campaign.name,
    state: campaign.state,
    budget: String(campaign.budget),
    biddingStrategy: campaignBiddingStrategyFromRaw(campaign.raw),
  };
}

export function CampaignSettingsSection({
  profileId,
  campaign,
  onUpdated,
}: CampaignSettingsSectionProps) {
  const { showToast } = useToast();
  const original = useMemo(() => draftFromCampaign(campaign), [campaign]);
  const rows = useMemo(() => campaignSettingsRows(campaign), [campaign]);
  // null = view mode.
  const [draft, setDraft] = useState<CampaignSettingsDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const patch = draft ? buildCampaignSettingsPatch(original, draft) : {};
  const hasChanges = Object.keys(patch).length > 0;
  const canEdit = campaign.state !== 'ARCHIVED';

  const mutation = useMutation({
    mutationFn: () =>
      saveCampaignSettings(profileId, campaign.campaignId, campaign.adProduct, patch),
    onSuccess: () => {
      showToast('Campaign settings updated', 'success');
      setDraft(null);
      setFormError(null);
      onUpdated?.();
    },
    onError: (error) => {
      const message = campaignBudgetApiErrorMessage(error, 'Failed to update campaign settings');
      setFormError(message);
      showToast(`Failed to update campaign settings: ${message}`, 'error');
    },
  });

  const updateDraft = (changes: Partial<CampaignSettingsDraft>) => {
    if (!draft) return;
    setDraft({ ...draft, ...changes });
    setFormError(null);
  };

  const save = () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      setFormError('Name is required');
      return;
    }
    const budget = Number(draft.budget);
    if (draft.budget.trim() === '' || !Number.isFinite(budget)) {
      setFormError('Enter a valid daily budget');
      return;
    }
    if (patch.budget !== undefined) {
      const budgetError = validateCampaignBudget(patch.budget, campaign.adProduct, campaign.budgetType);
      if (budgetError) {
        setFormError(budgetError);
        return;
      }
    }
    mutation.mutate();
  };

  const renderEditor = (field: EditableField) => {
    if (!draft) return null;
    const disabled = mutation.isPending;
    switch (field) {
      case 'name':
        return (
          <Input
            value={draft.name}
            disabled={disabled}
            onChange={(event) => updateDraft({ name: event.target.value })}
            aria-label="Campaign name"
            className="h-8 py-1 text-sm"
          />
        );
      case 'state':
        // Archived or unknown states can't be switched from here.
        if (!isEditableCampaignState(original.state)) return null;
        return (
          <Select
            value={draft.state}
            disabled={disabled}
            onChange={(event) => updateDraft({ state: event.target.value })}
            aria-label="Campaign state"
            className="h-8 py-1 text-sm"
          >
            <option value="ENABLED">Enabled</option>
            <option value="PAUSED">Paused</option>
          </Select>
        );
      case 'biddingStrategy':
        return (
          <div className="space-y-1">
            <Select
              value={draft.biddingStrategy ?? ''}
              disabled={disabled}
              onChange={(event) =>
                updateDraft({ biddingStrategy: event.target.value as SpBiddingStrategy })
              }
              aria-label="Bidding strategy"
              className="h-8 w-full py-1 text-sm"
            >
              {draft.biddingStrategy ? null : (
                <option value="" disabled>
                  Select a strategy
                </option>
              )}
              {BIDDING_STRATEGY_OPTIONS.map((strategy) => (
                <option key={strategy} value={strategy}>
                  {campaignBiddingStrategyCopy(strategy).label}
                </option>
              ))}
            </Select>
            {draft.biddingStrategy ? (
              <p className="text-xs text-foreground-subtle font-sans">
                {campaignBiddingStrategyCopy(draft.biddingStrategy).description}
              </p>
            ) : null}
          </div>
        );
      case 'budget':
        return (
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={draft.budget}
            disabled={disabled}
            onChange={(event) => updateDraft({ budget: event.target.value })}
            aria-label="Daily budget"
            className="h-8 w-40 py-1 text-sm"
          />
        );
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold text-foreground">Campaign settings</h2>
        {draft ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => {
                setDraft(null);
                setFormError(null);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={!hasChanges || mutation.isPending} onClick={save}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        ) : canEdit ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDraft(original)}
            className="inline-flex items-center gap-1.5"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Edit
          </Button>
        ) : null}
      </div>
      {formError ? <p className="text-sm text-error mb-3">{formError}</p> : null}
      <div className="max-h-[32rem] overflow-x-auto overflow-y-auto rounded-xl border border-border">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
              <th className="px-3 py-2">Setting</th>
              <th className="px-3 py-2">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => {
              const editor = row.field ? renderEditor(row.field) : null;
              return (
                <tr key={row.label} className="bg-surface hover:bg-page/80">
                  <td className="px-3 py-2 text-foreground-muted align-middle">{row.label}</td>
                  <td className="px-3 py-2 text-foreground font-mono text-[13px] break-all align-middle">
                    {editor ?? row.value}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
